import { sortWorkspaceDirectoryEntries } from "../workspace/workspaceTreeSort.js";

const MAX_OPEN_BYTES = 15 * 1024 * 1024;

export type OpenWorkspaceFileFn = (
  relativePath: string,
  fileHandle: FileSystemFileHandle,
) => void | Promise<void>;

/**
 * Left “Folder as Workspace” panel: lazy tree of files under a `FileSystemDirectoryHandle`.
 */
export class FolderWorkspacePane {
  readonly element: HTMLElement;
  private readonly header: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly treeHost: HTMLElement;
  private rootHandle: FileSystemDirectoryHandle | null = null;
  private onOpenFile: OpenWorkspaceFileFn | null = null;
  private onClose: (() => void) | null = null;
  private readonly fileClickData = new WeakMap<HTMLButtonElement, { relPath: string; handle: FileSystemFileHandle }>();
  private readonly rowMeta = new WeakMap<
    HTMLButtonElement,
    {
      kind: "file" | "directory";
      name: string;
      relPath: string;
      parentDir: FileSystemDirectoryHandle;
      fileHandle?: FileSystemFileHandle;
      dirHandle?: FileSystemDirectoryHandle;
    }
  >();
  private readonly contextMenu: HTMLDivElement;
  private contextTarget:
    | {
        kind: "file" | "directory";
        name: string;
        relPath: string;
        parentDir: FileSystemDirectoryHandle;
        fileHandle?: FileSystemFileHandle;
        dirHandle?: FileSystemDirectoryHandle;
      }
    | null = null;
  private readonly onDocPointerDown: (e: PointerEvent) => void;
  private readonly onDocKeyDown: (e: KeyboardEvent) => void;

  constructor(host: HTMLElement) {
    host.replaceChildren();
    this.element = document.createElement("aside");
    this.element.className = "npp-folder-workspace";
    this.element.setAttribute("role", "navigation");
    this.element.setAttribute("aria-label", "Folder workspace");
    this.element.hidden = true;

    this.header = document.createElement("div");
    this.header.className = "npp-folder-workspace-header";

    this.titleEl = document.createElement("span");
    this.titleEl.className = "npp-folder-workspace-title";

    this.closeBtn = document.createElement("button");
    this.closeBtn.type = "button";
    this.closeBtn.className = "npp-folder-workspace-close";
    this.closeBtn.setAttribute("aria-label", "Close folder workspace");
    this.closeBtn.textContent = "×";
    this.closeBtn.addEventListener("click", () => this.closeFromUser());

    this.header.append(this.titleEl, this.closeBtn);

    this.treeHost = document.createElement("div");
    this.treeHost.className = "npp-folder-workspace-tree";
    this.treeHost.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.contextTarget = null;
      this.showContextMenu(e.clientX, e.clientY);
    });

    this.contextMenu = document.createElement("div");
    this.contextMenu.className = "npp-ws-context-menu";
    this.contextMenu.hidden = true;
    this.contextMenu.setAttribute("role", "menu");

    this.element.append(this.header, this.treeHost, this.contextMenu);
    host.appendChild(this.element);

    this.onDocPointerDown = (e: PointerEvent) => {
      if (this.contextMenu.hidden) return;
      const t = e.target as Node | null;
      if (t && this.contextMenu.contains(t)) return;
      this.hideContextMenu();
    };
    this.onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || this.contextMenu.hidden) return;
      e.preventDefault();
      this.hideContextMenu();
    };
    document.addEventListener("pointerdown", this.onDocPointerDown, true);
    document.addEventListener("keydown", this.onDocKeyDown, true);
  }

  destroy(): void {
    document.removeEventListener("pointerdown", this.onDocPointerDown, true);
    document.removeEventListener("keydown", this.onDocKeyDown, true);
    this.element.remove();
  }

  /** Rebuild the file tree from the current workspace root (e.g. after git checkout). */
  async refreshFileTree(): Promise<void> {
    const h = this.rootHandle;
    if (!h) return;
    this.treeHost.replaceChildren();
    const ul = document.createElement("ul");
    ul.className = "npp-ws-tree";
    this.treeHost.appendChild(ul);
    await this.populateUl(ul, h, "");
  }

  setOnOpenFile(fn: OpenWorkspaceFileFn | null): void {
    this.onOpenFile = fn;
  }

  setOnClose(fn: (() => void) | null): void {
    this.onClose = fn;
  }

  getRoot(): FileSystemDirectoryHandle | null {
    return this.rootHandle;
  }

  private resetView(): void {
    this.rootHandle = null;
    this.treeHost.replaceChildren();
    this.titleEl.textContent = "";
    this.element.hidden = true;
  }

  /** User clicked × — clears tree and notifies host. */
  closeFromUser(): void {
    this.hideContextMenu();
    this.resetView();
    this.onClose?.();
  }

  async setRoot(handle: FileSystemDirectoryHandle | null): Promise<void> {
    this.resetView();
    if (!handle) return;
    this.rootHandle = handle;
    let perm = await handle.queryPermission({ mode: "readwrite" });
    if (perm !== "granted") {
      perm = await handle.requestPermission({ mode: "readwrite" });
    }
    if (perm !== "granted") {
      perm = await handle.queryPermission({ mode: "read" });
      if (perm !== "granted") perm = await handle.requestPermission({ mode: "read" });
      if (perm !== "granted") {
        void alert("Could not read the selected folder (permission denied).");
        this.resetView();
        return;
      }
    }
    this.element.hidden = false;
    this.titleEl.textContent = handle.name;

    const ul = document.createElement("ul");
    ul.className = "npp-ws-tree";
    this.treeHost.appendChild(ul);
    await this.populateUl(ul, handle, "");
  }

  private async populateUl(
    ul: HTMLUListElement,
    dirHandle: FileSystemDirectoryHandle,
    pathPrefix: string,
  ): Promise<void> {
    const metas: { name: string; kind: FileSystemHandleKind }[] = [];
    try {
      for await (const [name, h] of dirHandle.entries()) {
        metas.push({ name, kind: h.kind });
      }
    } catch {
      const li = document.createElement("li");
      li.className = "npp-ws-tree-error";
      li.textContent = "(could not read folder)";
      ul.appendChild(li);
      return;
    }

    const sorted = sortWorkspaceDirectoryEntries(metas);
    for (const { name, kind } of sorted) {
      const rel = pathPrefix ? `${pathPrefix}/${name}` : name;
      const li = document.createElement("li");
      li.className = "npp-ws-tree-li";

      if (kind === "directory") {
        let childDir: FileSystemDirectoryHandle;
        try {
          childDir = await dirHandle.getDirectoryHandle(name);
        } catch {
          continue;
        }
        const row = document.createElement("button");
        row.type = "button";
        row.className = "npp-ws-tree-row npp-ws-tree-folder";
        row.setAttribute("aria-expanded", "false");

        const chev = document.createElement("span");
        chev.className = "npp-ws-tree-chevron";
        chev.textContent = "▸";
        const label = document.createElement("span");
        label.className = "npp-ws-tree-label";
        label.textContent = name;
        row.append(chev, label);
        this.rowMeta.set(row, {
          kind: "directory",
          name,
          relPath: rel.replace(/\\/g, "/"),
          parentDir: dirHandle,
          dirHandle: childDir,
        });
        row.addEventListener("contextmenu", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.contextTarget = this.rowMeta.get(row) ?? null;
          this.showContextMenu(e.clientX, e.clientY);
        });

        const childUl = document.createElement("ul");
        childUl.className = "npp-ws-tree-children";
        childUl.hidden = true;

        let loaded = false;
        row.addEventListener("click", async () => {
          const open = row.getAttribute("aria-expanded") === "true";
          if (open) {
            row.setAttribute("aria-expanded", "false");
            childUl.hidden = true;
            chev.textContent = "▸";
            return;
          }
          row.setAttribute("aria-expanded", "true");
          childUl.hidden = false;
          chev.textContent = "▾";
          if (!loaded) {
            loaded = true;
            await this.populateUl(childUl, childDir, rel);
          }
        });

        li.append(row, childUl);
      } else {
        let fileHandle: FileSystemFileHandle;
        try {
          fileHandle = await dirHandle.getFileHandle(name);
        } catch {
          continue;
        }
        const row = document.createElement("button");
        row.type = "button";
        row.className = "npp-ws-tree-row npp-ws-tree-file";
        const label = document.createElement("span");
        label.className = "npp-ws-tree-label";
        label.textContent = name;
        row.appendChild(label);
        this.fileClickData.set(row, { relPath: rel.replace(/\\/g, "/"), handle: fileHandle });
        this.rowMeta.set(row, {
          kind: "file",
          name,
          relPath: rel.replace(/\\/g, "/"),
          parentDir: dirHandle,
          fileHandle,
        });
        row.addEventListener("contextmenu", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.contextTarget = this.rowMeta.get(row) ?? null;
          this.showContextMenu(e.clientX, e.clientY);
        });
        row.addEventListener("click", () => void this.tryOpenFileRow(row));
        li.appendChild(row);
      }

      ul.appendChild(li);
    }
  }

  private async tryOpenFileRow(row: HTMLButtonElement): Promise<void> {
    const data = this.fileClickData.get(row);
    const fn = this.onOpenFile;
    if (!data || !fn) return;
    let f: File;
    try {
      f = await data.handle.getFile();
    } catch {
      void alert(`Could not read "${data.relPath}".`);
      return;
    }
    if (f.size > MAX_OPEN_BYTES) {
      void alert(
        `"${data.relPath}" is too large to open here (${(f.size / (1024 * 1024)).toFixed(1)} MiB; max ${MAX_OPEN_BYTES / (1024 * 1024)} MiB).`,
      );
      return;
    }
    await fn(data.relPath, data.handle);
  }

  private hideContextMenu(): void {
    this.contextMenu.hidden = true;
  }

  private addMenuItem(label: string, run: () => void | Promise<void>): void {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "npp-ws-context-menu-item";
    b.textContent = label;
    b.setAttribute("role", "menuitem");
    b.addEventListener("click", () => {
      this.hideContextMenu();
      void run();
    });
    this.contextMenu.appendChild(b);
  }

  private addMenuSep(): void {
    const s = document.createElement("div");
    s.className = "npp-ws-context-menu-separator";
    this.contextMenu.appendChild(s);
  }

  private isValidEntryName(name: string): boolean {
    return Boolean(name) && name !== "." && name !== ".." && !/[\\/]/.test(name)
  }

  private async createFileAt(dir: FileSystemDirectoryHandle): Promise<void> {
    const name = prompt("New file name:");
    if (!name) return;
    const n = name.trim();
    if (!this.isValidEntryName(n)) {
      void alert("Invalid file name.");
      return;
    }
    try {
      await dir.getFileHandle(n, { create: false });
      void alert(`"${n}" already exists.`);
      return;
    } catch {
      // not found -> create
    }
    await dir.getFileHandle(n, { create: true });
    await this.refreshFileTree();
  }

  private async createFolderAt(dir: FileSystemDirectoryHandle): Promise<void> {
    const name = prompt("New folder name:");
    if (!name) return;
    const n = name.trim();
    if (!this.isValidEntryName(n)) {
      void alert("Invalid folder name.");
      return;
    }
    try {
      await dir.getDirectoryHandle(n, { create: false });
      void alert(`"${n}" already exists.`);
      return;
    } catch {
      // not found -> create
    }
    await dir.getDirectoryHandle(n, { create: true });
    await this.refreshFileTree();
  }

  private async copyFileHandle(src: FileSystemFileHandle, dst: FileSystemFileHandle): Promise<void> {
    const f = await src.getFile();
    const w = await dst.createWritable();
    await w.write(f);
    await w.close();
  }

  private async copyDirRecursive(src: FileSystemDirectoryHandle, dst: FileSystemDirectoryHandle): Promise<void> {
    for await (const [name, h] of src.entries()) {
      if (h.kind === "directory") {
        const s = await src.getDirectoryHandle(name);
        const d = await dst.getDirectoryHandle(name, { create: true });
        await this.copyDirRecursive(s, d);
      } else {
        const s = await src.getFileHandle(name);
        const d = await dst.getFileHandle(name, { create: true });
        await this.copyFileHandle(s, d);
      }
    }
  }

  private async renameTarget(): Promise<void> {
    const t = this.contextTarget;
    if (!t) return;
    const name = prompt(`Rename "${t.name}" to:`, t.name);
    if (!name) return;
    const next = name.trim();
    if (!this.isValidEntryName(next)) {
      void alert("Invalid name.");
      return;
    }
    if (next === t.name) return;
    try {
      await t.parentDir.getFileHandle(next, { create: false });
      void alert(`"${next}" already exists.`);
      return;
    } catch {
      // ignore
    }
    try {
      await t.parentDir.getDirectoryHandle(next, { create: false });
      void alert(`"${next}" already exists.`);
      return;
    } catch {
      // ignore
    }
    if (t.kind === "file" && t.fileHandle) {
      const dst = await t.parentDir.getFileHandle(next, { create: true });
      await this.copyFileHandle(t.fileHandle, dst);
      await t.parentDir.removeEntry(t.name);
    } else if (t.kind === "directory" && t.dirHandle) {
      const dst = await t.parentDir.getDirectoryHandle(next, { create: true });
      await this.copyDirRecursive(t.dirHandle, dst);
      await t.parentDir.removeEntry(t.name, { recursive: true });
    }
    await this.refreshFileTree();
  }

  private async deleteTarget(): Promise<void> {
    const t = this.contextTarget;
    if (!t) return;
    const ok = confirm(`Delete "${t.relPath}"?`);
    if (!ok) return;
    await t.parentDir.removeEntry(t.name, { recursive: t.kind === "directory" });
    await this.refreshFileTree();
  }

  private async openTarget(): Promise<void> {
    const t = this.contextTarget;
    if (!t || t.kind !== "file" || !t.fileHandle) return;
    const fakeRow = document.createElement("button");
    this.fileClickData.set(fakeRow, { relPath: t.relPath, handle: t.fileHandle });
    await this.tryOpenFileRow(fakeRow);
  }

  private showContextMenu(x: number, y: number): void {
    this.contextMenu.replaceChildren();
    const t = this.contextTarget;
    const dirForNew = t?.kind === "directory" ? t.dirHandle : t?.parentDir ?? this.rootHandle;
    if (t?.kind === "file") this.addMenuItem("Open", () => this.openTarget());
    if (dirForNew) {
      this.addMenuItem("New File", () => this.createFileAt(dirForNew));
      this.addMenuItem("New Folder", () => this.createFolderAt(dirForNew));
    }
    if (t) {
      this.addMenuSep();
      this.addMenuItem("Rename", () => this.renameTarget());
      this.addMenuItem("Delete", () => this.deleteTarget());
      this.addMenuItem("Copy Relative Path", async () => {
        await navigator.clipboard.writeText(t.relPath);
      });
    }
    this.addMenuSep();
    this.addMenuItem("Refresh", () => this.refreshFileTree());
    this.contextMenu.style.visibility = "hidden";
    this.contextMenu.hidden = false;
    const w = this.contextMenu.offsetWidth;
    const h = this.contextMenu.offsetHeight;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;
    let cx = Math.max(0, x);
    if (cx + w > vpW) cx = Math.max(0, vpW - w);
    let cy = y;
    if (cy + h > vpH) {
      const above = y - h;
      cy = above >= 0 ? above : Math.max(0, vpH - h);
    }
    this.contextMenu.style.left = `${cx}px`;
    this.contextMenu.style.top = `${cy}px`;
    this.contextMenu.style.visibility = "";
  }
}
